/* eslint-disable no-nested-ternary */
import i18next from 'i18next';
import {
  InputNumber,
  Radio,
  Select,
  Switch,
  Space,
  Form,
  notification
} from 'antd';
import React, { useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import numeral from 'numeral';
import PreRequisitesSwitch from './PreRequisitesSwitch';
import useQuotesContext from './QuotesContext';
import FormRadioButtonWithCustom from '../../components/FormRadioButtonWithCustom/FormRadioButtonWithCustom';
import { preRequisitesLayout } from '../../utils/constants/formLayout';
import { numberWithSpaces } from '../../utils/formatValue';
import { arrayConditionnalElement } from '../../utils/arrayConditionnalElement';
import {
  inputNumberFormatter,
  inputNumberParser
} from '../../utils/inputNumberFormatter';

const { Option } = Select;

export const activityRCPHXTypeLabelMap = {
  TECH: i18next.t('contracts.rcphx.activity_type.TECH'),
  MAC: i18next.t('contracts.rcphx.activity_type.MAC'),
  MAN: i18next.t('contracts.rcphx.activity_type.MAN'),
  MISC: i18next.t('contracts.rcphx.activity_type.MISC'),
  MEDIA: i18next.t('contracts.rcphx.activity_type.MEDIA'),
  TAG: i18next.t('contracts.rcphx.activity_type.TAG'),
  SECU: i18next.t('contracts.rcphx.activity_type.SECU'),
  BIE: i18next.t('contracts.rcphx.activity_type.BIE'),
  MED: i18next.t('contracts.rcphx.activity_type.MED'),
  OSTEO: i18next.t('contracts.rcphx.activity_type.OSTEO')
};

export const RCPHX_TATOO_ACTIVITY =
  'Tatoueur / Perceur ; Maquillage permanent et semi-permanent ; Formation à ces métiers ; Vente de bijoux fantaisies d’une valeur unitaire inférieur à 180€';

export const RCPHX_BIE_TATOO_ACTIVITY =
  'Tatoueur/Perceurs : Tatouages ; Microblading ; Maquillage permanent et semi-permanent ; Perçage (à l’exclusion de tout piercing micro dermal) ; Extensions et rehaussement de cils ; Extensions et rehaussement de cils ; Formation au métier de tatoueur, perceur, maquillage permanent et semi-permanent, microblading ; Vente de bijoux fantaisies d’une valeur unitaire inférieur à 180€.';

export const RCPHX_VTM_ACTIVITY =
  'Transport de marchandises en véhicules terrestres à moteur de moins de 3,5 tonnes';
export const RCPHX_VTM_MAX_TURNOVER = 300000;

const RCPHX_BORLOO_ACTIVITY_MARKER = 'dispositif loi Borloo';
const RCPHX_NON_BORLOO_ACTIVITY_MARKER = `hors ${RCPHX_BORLOO_ACTIVITY_MARKER}`;

export const includesVtmActivity = (activityDetail) =>
  !!activityDetail?.includes(RCPHX_VTM_ACTIVITY);

export const includesBorlooActivity = (activityDetail) =>
  !!activityDetail?.some(
    (activity) =>
      activity.includes(RCPHX_BORLOO_ACTIVITY_MARKER) &&
      !activity.includes(RCPHX_NON_BORLOO_ACTIVITY_MARKER)
  );

export const RCPHX_BIE_SPORT_WELLNESS_KEYWORDS = [
  'Coach ou éducateur sportif',
  'Yoga',
  'Gym Bien',
  'Marche Afghane',
  'Pilates',
  'Tai Chi Chuan',
  'Tai Ji Quan'
];

const activitiesType = [
  {
    label: activityRCPHXTypeLabelMap.TECH,
    value: 'TECH'
  },
  {
    label: activityRCPHXTypeLabelMap.MAC,
    value: 'MAC'
  },
  {
    label: activityRCPHXTypeLabelMap.MISC,
    value: 'MISC'
  },
  {
    label: activityRCPHXTypeLabelMap.MAN,
    value: 'MAN'
  },
  {
    label: activityRCPHXTypeLabelMap.MEDIA,
    value: 'MEDIA'
  },
  {
    label: activityRCPHXTypeLabelMap.TAG,
    value: 'TAG'
  },
  {
    label: activityRCPHXTypeLabelMap.SECU,
    value: 'SECU'
  },
  {
    label: activityRCPHXTypeLabelMap.BIE,
    value: 'BIE'
  },
  {
    label: activityRCPHXTypeLabelMap.MED,
    value: 'MED'
  },
  {
    label: activityRCPHXTypeLabelMap.OSTEO,
    value: 'OSTEO'
  }
];

// If turnover > 500 000€ can't have guarantee limit less than 300 000€
const MAX_FULL_GUARANTEE_LIMIT = 500000;
// LOI 50K CA thresholds
const MAX_TURNOVER_LOI_50K = 50000;
const MAX_TURNOVER_LOI_50K_TECH_MAC_MEDIA = 80000;
// MED / BIE / OSTEO subscription is limited to 200 000€ of annual turnover, no dérogation possible above
const RCPHX_MED_BIE_OSTEO_MAX_TURNOVER = 200000;
const RCPHX_MED_BIE_OSTEO_TYPES = ['MED', 'BIE', 'OSTEO'];
const VTM_GUARANTEE_LIMIT_STEPS = [50000, 100000, 200000];
const guaranteeLimitSteps = (
  hasTattooActivity,
  type,
  turnover,
  hasVtmActivity = false
) => {
  // MED / OSTEO / BIE have a single fixed LOI (8M€), no palier selection
  if (type === 'MED' || type === 'OSTEO' || type === 'BIE') {
    return [
      {
        value: 8000000,
        label: '8 000 000€',
        disabled: () => false
      }
    ];
  }

  // VTM < 3,5t is only sold at 3 fixed LOI tiers (cf. calculette dédiée)
  if (hasVtmActivity) {
    return VTM_GUARANTEE_LIMIT_STEPS.map((value) => ({
      value,
      label: `${value.toLocaleString('fr-FR')}€`,
      disabled: () => false
    }));
  }

  return [
    {
      value: 50000,
      label: '50 000€',
      disabled: () => {
        if (type === 'TAG' || type === 'SECU' || hasTattooActivity) return true;
        if (type === 'TECH' || type === 'MAC' || type === 'MEDIA')
          return turnover > MAX_TURNOVER_LOI_50K_TECH_MAC_MEDIA;
        return turnover > MAX_TURNOVER_LOI_50K;
      }
    },
    {
      value: 100000,
      label: '100 000€',
      disabled: () =>
        turnover > MAX_FULL_GUARANTEE_LIMIT ||
        type === 'TAG' ||
        type === 'SECU'
    },
    {
      value: 200000,
      label: '200 000€',
      disabled: () =>
        turnover > MAX_FULL_GUARANTEE_LIMIT ||
        type === 'TAG' ||
        type === 'SECU'
    },
    {
      value: 300000,
      label: '300 000€',
      disabled: () => type === 'TAG'
    },
    {
      value: 400000,
      label: '400 000€',
      disabled: () => hasTattooActivity || type === 'TAG'
    },
    {
      value: 500000,
      label: '500 000€',
      disabled: () => hasTattooActivity || type === 'TAG'
    },
    {
      value: 750000,
      label: '750 000€',
      disabled: () => hasTattooActivity || type === 'TAG'
    },
    {
      value: 1000000,
      label: '1 000 000€',
      disabled: () => hasTattooActivity || type === 'TAG'
    },
    {
      value: 1500000,
      label: '1 500 000€',
      disabled: () => hasTattooActivity || type === 'SECU'
    },
    {
      value: 2000000,
      label: '2 000 000€',
      disabled: () => hasTattooActivity || type === 'SECU'
    },
    {
      value: 3000000,
      label: '3 000 000€',
      disabled: () => hasTattooActivity || type === 'SECU'
    },
    {
      value: 3000001,
      label: '+ de 3 000 000€',
      disabled: () => hasTattooActivity || type === 'SECU'
    }
  ];
};

export const RCPHX_TUNROVER_EXEMPTION_THRESHOLD_MIN = 3000001;
export const RCPHX_TUNROVER_EXEMPTION_THRESHOLD_MAX = 5000001;
export const RCPHX_TUNROVER_EXEMPTION_THRESHOLD_TATTOO = 500001;
export const RCPHX_TUNROVER_EXEMPTION_THRESHOLD_MAX_SECU = 1_000_000;

export const normalizeActivityName = (value = '') =>
  value
    .normalize('NFC')
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

// The turnover max depends on the risk of activities
export const getRCPHXTurnoverExemptionThreshold = (
  activitiesHiscox = [],
  activity_type,
  activity_detail,
  turnover,
  guarantee_limit
) => {
  if (activity_detail) {
    const riskCategoryList = [];

    // If one of the activites is tattoo
    if (
      activity_detail.includes(
        'Tatoueur / Perceur ; Maquillage permanent et semi-permanent ; Formation à ces métiers ; Vente de bijoux fantaisies d’une valeur unitaire inférieur à 180€'
      )
    ) {
      return RCPHX_TUNROVER_EXEMPTION_THRESHOLD_TATTOO;
    }

    const typeActivities = activitiesHiscox.filter(
      ({ type }) => type === activity_type
    );

    activity_detail.forEach((activity) => {
      const normalizedActivity = normalizeActivityName(activity);
      const activityInfos = typeActivities.find(
        ({ name }) => normalizeActivityName(name) === normalizedActivity
      );

      if (activityInfos) {
        const { risk_category_low, risk_category_high } = activityInfos;
        if (turnover <= 1500000 && guarantee_limit <= 1000000) {
          riskCategoryList.push(risk_category_low);
        } else {
          riskCategoryList.push(risk_category_high);
        }
      }
    });
    const higherRisk = Math.max(...riskCategoryList);

    if (higherRisk <= 2) {
      return RCPHX_TUNROVER_EXEMPTION_THRESHOLD_MAX;
    }
    return RCPHX_TUNROVER_EXEMPTION_THRESHOLD_MIN;
  }
  return RCPHX_TUNROVER_EXEMPTION_THRESHOLD_MIN;
};

// Franchise RCE (excess), purely informative display:
// - MED / OSTEO / BIE: always 0€
// - SECU: always 500€
// - other types: 0€ if turnover <= 1 500 000€ AND guarantee_limit <= 1 000 000€, else 500€
export const RCPHX_DEDUCTIBLE_AMOUNT_RCE_LOW = 0;
export const RCPHX_DEDUCTIBLE_AMOUNT_RCE_HIGH = 500;
export const getRCPHXDeductibleAmountRCE = (
  activity_type,
  turnover,
  guarantee_limit
) => {
  if (['MED', 'OSTEO', 'BIE'].includes(activity_type)) {
    return RCPHX_DEDUCTIBLE_AMOUNT_RCE_LOW;
  }
  if (activity_type === 'SECU') {
    return RCPHX_DEDUCTIBLE_AMOUNT_RCE_HIGH;
  }
  if (turnover <= 1500000 && guarantee_limit <= 1000000) {
    return RCPHX_DEDUCTIBLE_AMOUNT_RCE_LOW;
  }
  return RCPHX_DEDUCTIBLE_AMOUNT_RCE_HIGH;
};

export const getRCPHXSpecificTurnoverExemption = (activity_type, turnover) => {
  if (activity_type === 'TAG' && turnover <= 1500000) {
    return false;
  } else if (activity_type === 'SECU' && turnover > 1500000) {
    return false;
  }
};

export const RCPHX_GUARANTEE_LIMIT_EXEMPTION_THRESHOLD = 3000001;
export const RCPHX_GUARANTEE_LIMIT_EXEMPTION_THRESHOLD_MIN_TAG = 1_000_000;
export const RCPHX_GUARANTEE_LIMIT_EXEMPTION_THRESHOLD_MAX_SECU = 1_000_000;
const DEDUCTIBLE_AMOUNT_USA_CANADA = 15000;

const worldwideAllowedActivities = ['TECH', 'MAN', 'MAC', 'MEDIA', 'TAG'];
export { worldwideAllowedActivities as RCPHX_WORLDWIDE_ALLOWED_ACTIVITIES };

const cyberOptionAllowedActivities = ['TECH', 'MAN', 'MAC', 'MEDIA', 'BIE', 'MISC', 'TAG', 'SECU', 'MED', 'OSTEO'];
export { cyberOptionAllowedActivities as RCPHX_CYBER_ALLOWED_ACTIVITIES };

const activtyPrerequisites = {
  TECH: {
    base: {
      label: `activity_hiscox_tech`,
      name: 'activity_hiscox_tech'
    },
    world: {
      label: `activity_hiscox_tech_world`,
      name: 'activity_hiscox_tech_world'
    },
    ia: {
      label: `activity_hiscox_tech_ia`,
      name: 'activity_hiscox_tech_ia'
    },
    exposure: {
      label: `geographic_exposure_hiscox_tech`,
      name: 'geographic_exposure_hiscox'
    },
    exposure_world: {
      label: `geographic_exposure_hiscox_tech_world`,
      name: 'geographic_exposure_hiscox_world'
    }
  },
  MAN: {
    base: {
      label: `activity_hiscox_man`,
      name: 'activity_hiscox_man'
    },
    world: {
      label: `activity_hiscox_man_world`,
      name: 'activity_hiscox_man_world'
    },
    exposure: {
      label: `geographic_exposure_hiscox_man`,
      name: 'geographic_exposure_hiscox'
    },
    exposure_world: {
      label: `geographic_exposure_hiscox_man_world`,
      name: 'geographic_exposure_hiscox_world'
    }
  },
  MISC: {
    base: {
      label: `activity_hiscox_misc`,
      name: 'activity_hiscox_misc'
    },
    transport: {
      label: `activity_transport_misc`,
      name: 'activity_transport_misc'
    },
    borloo: {
      label: `activity_borloo_misc`,
      name: 'activity_borloo_misc'
    },
    exposure: {
      label: `geographic_exposure_hiscox_misc`,
      name: 'geographic_exposure_hiscox'
    }
  },
  BIE: {
    base: {
      label: `activity_hiscox_bie`,
      name: 'activity_hiscox_bie'
    },
    qualification: {
      label: `activity_hiscox_bie_qualification`,
      name: 'activity_hiscox_bie_qualification'
    },
    sport: {
      label: `activity_hiscox_bie_sport`,
      name: 'activity_hiscox_bie_sport'
    },
    exposure: {
      label: `geographic_exposure_hiscox_misc`,
      name: 'geographic_exposure_hiscox'
    }
  },
  BIE_TATTOO: {
    eligibility: {
      label: `activity_hiscox_bie_tattoo_eligibility`,
      name: 'activity_hiscox_bie_tattoo_eligibility'
    },
    base: {
      label: `activity_hiscox_bie_tattoo`,
      name: 'activity_hiscox_bie_tattoo'
    },
    exposure: {
      label: `geographic_exposure_hiscox_misc`,
      name: 'geographic_exposure_hiscox'
    },
    compliance: {
      label: `compliance_hiscox_bie_tattoo`,
      name: 'compliance_hiscox_bie_tattoo'
    }
  },
  MISC_TATTOO: {
    base: {
      label: `activity_hiscox_misc_tattoo`,
      name: 'activity_hiscox_misc_tattoo'
    },
    exposure: {
      label: `geographic_exposure_hiscox_misc_tattoo`,
      name: 'geographic_exposure_hiscox'
    },
    compliance: {
      label: `compliance_hiscox_misc_tattoo`,
      name: 'compliance_hiscox_misc_tattoo'
    }
  },
  MAC: {
    base: {
      label: `activity_hiscox_mac`,
      name: 'activity_hiscox_mac'
    },
    world: {
      label: `activity_hiscox_mac_world`,
      name: 'activity_hiscox_mac_world'
    },
    exposure: {
      label: `geographic_exposure_hiscox_mac`,
      name: 'geographic_exposure_hiscox'
    },
    exposure_world: {
      label: `geographic_exposure_hiscox_mac_world`,
      name: 'geographic_exposure_hiscox_world'
    }
  },
  MEDIA: {
    base: {
      label: `activity_hiscox_med`,
      name: 'activity_hiscox_med'
    },
    world: {
      label: `activity_hiscox_med_world`,
      name: 'activity_hiscox_med_world'
    },
    exposure: {
      label: `geographic_exposure_hiscox_med`,
      name: 'geographic_exposure_hiscox'
    },
    exposure_world: {
      label: `geographic_exposure_hiscox_med_world`,
      name: 'geographic_exposure_hiscox_world'
    }
  },
  TAG: {
    base: {
      label: `activity_hiscox_tag`,
      name: 'activity_hiscox_tag'
    },
    world: {
      label: `activity_hiscox_tag_world`,
      name: 'activity_hiscox_tag_world'
    },
    exposure: {
      label: `geographic_exposure_hiscox_tag`,
      name: 'geographic_exposure_hiscox'
    },
    exposure_world: {
      label: `geographic_exposure_hiscox_tag_world`,
      name: 'geographic_exposure_hiscox_world'
    }
  },
  SECU: {
    base: {
      label: `activity_hiscox_secu`,
      name: 'activity_hiscox_secu'
    },
    exposure: {
      label: `geographic_exposure_hiscox_secu`,
      name: 'geographic_exposure_hiscox'
    }
  },
  MED: {
    base: {
      label: `activity_hiscox_med_paramedical`,
      name: 'activity_hiscox_med_paramedical'
    },
    exposure: {
      label: `geographic_exposure_hiscox_misc`,
      name: 'geographic_exposure_hiscox'
    }
  },
  OSTEO: {
    base: {
      label: `activity_hiscox_osteo`,
      name: 'activity_hiscox_osteo'
    }
  }
};

const CYBER_EXPOSURE_LIGHT_EXPORT_ONLY = {
  label: 'geographic_exposure_hiscox_cyber_light_export',
  name: 'geographic_exposure_hiscox_cyber'
};

const cyberExposureOverrides = {
  TECH: CYBER_EXPOSURE_LIGHT_EXPORT_ONLY,
  MAN: CYBER_EXPOSURE_LIGHT_EXPORT_ONLY,
  MAC: CYBER_EXPOSURE_LIGHT_EXPORT_ONLY,
  MEDIA: CYBER_EXPOSURE_LIGHT_EXPORT_ONLY,
  TAG: CYBER_EXPOSURE_LIGHT_EXPORT_ONLY,
  MISC: CYBER_EXPOSURE_LIGHT_EXPORT_ONLY,
  BIE: CYBER_EXPOSURE_LIGHT_EXPORT_ONLY,
  MED: CYBER_EXPOSURE_LIGHT_EXPORT_ONLY,
  OSTEO: {
    label: 'geographic_exposure_hiscox_cyber_light',
    name: 'geographic_exposure_hiscox_cyber'
  }
};

export const getInsuranceHistoricLabel = (activityType) =>
  activityType === 'SECU'
    ? 'insurance_historic_hiscox_secu'
    : 'insurance_historic_hiscox';

export const getActivityPrerequisites = (contract = {}) => {
  const {
    activity_type = 'TECH',
    activity_detail = [],
    turnover_usa_canada = false,
    turnover = 0,
    cyber_option_rcphx = false,
    cyber_option_rcphx_fraud = false
  } = contract || {};

  const prerequisitesArray = [];

  const worldwideProtection =
    turnover_usa_canada && worldwideAllowedActivities.includes(activity_type);

  const hasBieTattooActivity =
    activity_type === 'BIE' &&
    activity_detail?.includes(RCPHX_BIE_TATOO_ACTIVITY);
  const hasMiscTattooActivity =
    activity_type === 'MISC' && activity_detail?.includes(RCPHX_TATOO_ACTIVITY);
  const hasTattooActivity = hasBieTattooActivity || hasMiscTattooActivity;

  let activityPrerequisite = activtyPrerequisites[activity_type || 'TECH'];

  if (hasBieTattooActivity) {
    activityPrerequisite = activtyPrerequisites.BIE_TATTOO;
  } else if (hasMiscTattooActivity) {
    activityPrerequisite = activtyPrerequisites.MISC_TATTOO;
  } else if (activity_type === 'BIE') {
    activityPrerequisite = activtyPrerequisites.BIE;
  }

  if (hasBieTattooActivity) {
    prerequisitesArray.push(activtyPrerequisites.BIE_TATTOO.eligibility);
  }

  if (worldwideProtection) {
    prerequisitesArray.push(activityPrerequisite.world);
  } else {
    prerequisitesArray.push(activityPrerequisite.base);
  }

  if (activity_type === 'TECH' && turnover > 1_000_000) {
    prerequisitesArray.push(activtyPrerequisites.TECH.ia);
  }

  if (hasTattooActivity) {
    prerequisitesArray.push(activityPrerequisite.compliance);
  }

  if (activity_type === 'MISC' && includesVtmActivity(activity_detail)) {
    prerequisitesArray.push(activtyPrerequisites.MISC.transport);
  }

  if (activity_type === 'MISC' && includesBorlooActivity(activity_detail)) {
    prerequisitesArray.push(activtyPrerequisites.MISC.borloo);
  }

  if (activity_type === 'BIE' && !hasTattooActivity) {
    prerequisitesArray.push(activtyPrerequisites.BIE.qualification);

    if (
      activity_detail?.some((act) =>
        RCPHX_BIE_SPORT_WELLNESS_KEYWORDS.some((keyword) =>
          act.includes(keyword)
        )
      )
    ) {
      prerequisitesArray.push(activtyPrerequisites.BIE.sport);
    }
  }

  if (worldwideProtection) {
    prerequisitesArray.push(activityPrerequisite.exposure_world);
  } else if (activityPrerequisite.exposure) {
    prerequisitesArray.push(activityPrerequisite.exposure);
  }

  if (cyber_option_rcphx && cyberOptionAllowedActivities.includes(activity_type)) {
    prerequisitesArray.push(
      {
        label: cyber_option_rcphx_fraud
          ? 'activity_hiscox_cyber_fraud'
          : 'activity_hiscox_cyber',
        name: 'activity_hiscox_cyber'
      },
      {
        label: 'history_cyber_hiscox',
        name: 'history_cyber_hiscox'
      },
      cyberExposureOverrides[activity_type] || {
        label: 'geographic_exposure_hiscox_cyber',
        name: 'geographic_exposure_hiscox_cyber'
      }
    );
  }

  return prerequisitesArray;
};

const useRCPHXFields = (form, isRCPHX = false) => {
  const { t } = useTranslation();
  const {
    formValues,
    setFormValues,
    estimatedCost,
    activitiesHiscox = []
  } = useQuotesContext();

  const liveRcphxActivitiesDetails = useMemo(
    () =>
      activitiesHiscox.reduce((acc, { type, name }) => {
        if (!acc[type]) acc[type] = [];
        acc[type].push({ name });
        return acc;
      }, {}),
    [activitiesHiscox]
  );

  const turnoverWatcher = Form.useWatch('turnover', form);
  const activityTypeWatcher = Form.useWatch('activity_type', form);
  const worldwideWatcher = Form.useWatch('turnover_usa_canada', form);

  const isAmendment = window.location.href.search('amendment');
  const hasTattooActivity =
    formValues?.activity_detail?.includes(RCPHX_TATOO_ACTIVITY);
  const hasVtmActivity =
    formValues?.activity_type === 'MISC' &&
    formValues?.activity_detail?.includes(RCPHX_VTM_ACTIVITY);

  const typePrerequisites = useMemo(() => {
    return getActivityPrerequisites(formValues);
  }, [
    formValues?.activity_type,
    hasTattooActivity,
    formValues?.turnover_usa_canada,
    formValues?.turnover,
    formValues?.activity_detail,
    formValues?.cyber_option_rcphx,
    formValues?.cyber_option_rcphx_fraud
  ]);

  useEffect(() => {
    if (!worldwideAllowedActivities.includes(formValues?.activity_type)) {
      // If activity type is MISC can't have world option leag protection
      if (formValues?.legal_protection_usa_canada) {
        form.setFieldsValue({
          turnover_usa_canada: false,
          turnover_usa_canada_amount: null,
          turnover_usa_canada_amount_type: null
        });
        setFormValues({
          ...formValues,
          turnover_usa_canada: false,
          turnover_usa_canada_amount: null,
          turnover_usa_canada_amount_type: null
        });
      }

      if (hasTattooActivity) {
        form.setFieldsValue({
          legal_protection_rcphx: true,
          legal_protection_usa_canada: false,
          exploitation_responsibility: true
        });
        setFormValues({
          ...formValues,
          legal_protection_rcphx: true,
          legal_protection_usa_canada: false,
          exploitation_responsibility: true
        });
      }
    }
  }, [formValues?.activity_detail]);

  // RCE (exploitation_responsibility) is included, not optional, for MED and OSTEO
  useEffect(() => {
    if (
      ['MED', 'OSTEO', 'BIE'].includes(formValues?.activity_type) &&
      !formValues?.exploitation_responsibility
    ) {
      form.setFieldsValue({ exploitation_responsibility: true });
      setFormValues({ ...formValues, exploitation_responsibility: true });
    }
  }, [activityTypeWatcher]);

  useEffect(() => {
    if (
      !RCPHX_MED_BIE_OSTEO_TYPES.includes(formValues?.activity_type) &&
      !formValues?.legal_protection_rcphx
    ) {
      form.setFieldsValue({ legal_protection_rcphx: true });
      setFormValues({ ...formValues, legal_protection_rcphx: true });
    }
  }, [activityTypeWatcher]);

  useEffect(() => {
    if (
      !RCPHX_MED_BIE_OSTEO_TYPES.includes(formValues?.activity_type) &&
      formValues?.legal_protection_rcphx_extended
    ) {
      form.setFieldsValue({ legal_protection_rcphx_extended: false });
      setFormValues({ ...formValues, legal_protection_rcphx_extended: false });
    }
  }, [activityTypeWatcher]);

  // The extended option can only be taken on top of the base protection
  useEffect(() => {
    if (
      RCPHX_MED_BIE_OSTEO_TYPES.includes(formValues?.activity_type) &&
      !formValues?.legal_protection_rcphx &&
      formValues?.legal_protection_rcphx_extended
    ) {
      form.setFieldsValue({ legal_protection_rcphx_extended: false });
      setFormValues({ ...formValues, legal_protection_rcphx_extended: false });
    }
  }, [formValues?.legal_protection_rcphx]);

  useEffect(() => {
    if (
      !formValues?.cyber_option_rcphx &&
      formValues?.cyber_option_rcphx_extended
    ) {
      form.setFieldsValue({
        cyber_option_rcphx_extended: false,
        cyber_option_rcphx_fraud: false
      });
      setFormValues({
        ...formValues,
        cyber_option_rcphx_extended: false,
        cyber_option_rcphx_fraud: false
      });
    }
  }, [formValues?.cyber_option_rcphx]);

  useEffect(() => {
    if (
      !formValues?.cyber_option_rcphx_extended &&
      formValues?.cyber_option_rcphx_fraud
    ) {
      form.setFieldsValue({ cyber_option_rcphx_fraud: false });
      setFormValues({ ...formValues, cyber_option_rcphx_fraud: false });
    }
  }, [formValues?.cyber_option_rcphx_extended]);

  useEffect(() => {
    const cyberAllowed =
      cyberOptionAllowedActivities.includes(formValues?.activity_type) &&
      (formValues?.turnover || 0) <= 500_000;
    if (
      !cyberAllowed &&
      (formValues?.cyber_option_rcphx ||
        formValues?.cyber_option_rcphx_extended ||
        formValues?.cyber_option_rcphx_fraud)
    ) {
      form.setFieldsValue({
        cyber_option_rcphx: false,
        cyber_option_rcphx_extended: false,
        cyber_option_rcphx_fraud: false
      });
      setFormValues({
        ...formValues,
        cyber_option_rcphx: false,
        cyber_option_rcphx_extended: false,
        cyber_option_rcphx_fraud: false
      });
    }
  }, [activityTypeWatcher, turnoverWatcher]);

  useEffect(() => {
    if (
      RCPHX_MED_BIE_OSTEO_TYPES.includes(formValues?.activity_type) &&
      formValues?.turnover > RCPHX_MED_BIE_OSTEO_MAX_TURNOVER
    ) {
      form.setFieldsValue({ turnover: RCPHX_MED_BIE_OSTEO_MAX_TURNOVER });
      setFormValues({
        ...formValues,
        turnover: RCPHX_MED_BIE_OSTEO_MAX_TURNOVER
      });
      notification.warning({
        message:
          "Ce produit, pour cette famille d'activités, a une portée de souscription limitée à 200 000€ de chiffre d'affaires annuel.",
        duration: 7,
        key: 'RCPHX_MED_BIE_OSTEO_MAX_TURNOVER'
      });
    }
  }, [activityTypeWatcher, turnoverWatcher]);

  useEffect(() => {
    if (
      hasVtmActivity &&
      formValues?.turnover > RCPHX_VTM_MAX_TURNOVER
    ) {
      form.setFieldsValue({ turnover: RCPHX_VTM_MAX_TURNOVER });
      setFormValues({
        ...formValues,
        turnover: RCPHX_VTM_MAX_TURNOVER
      });
      notification.warning({
        message: t('contracts.form.rcphx_vtm_max_turnover_notification'),
        duration: 7,
        key: 'RCPHX_VTM_MAX_TURNOVER'
      });
    }
  }, [activityTypeWatcher, turnoverWatcher, hasVtmActivity]);

  useEffect(() => {
    if (
      RCPHX_MED_BIE_OSTEO_TYPES.includes(formValues?.activity_type) &&
      formValues?.guarantee_limit !== 8000000
    ) {
      form.setFieldsValue({ guarantee_limit: 8000000 });
      setFormValues({ ...formValues, guarantee_limit: 8000000 });
    }
  }, [activityTypeWatcher]);

  // When the user check turnover_usa_canada we should check the legal_protection_usa_canada input
  useEffect(() => {
    form.setFieldsValue({
      legal_protection_usa_canada: formValues?.turnover_usa_canada
    });
    setFormValues({
      ...formValues,
      legal_protection_usa_canada: formValues?.turnover_usa_canada
    });
  }, [formValues?.turnover_usa_canada]);

  // When the user check legal_protection_usa_canada we should check the turnover_usa_canada input
  useEffect(() => {
    if (formValues?.legal_protection_usa_canada) {
      form.setFieldsValue({
        turnover_usa_canada: formValues?.legal_protection_usa_canada
      });
      setFormValues({
        ...formValues,
        turnover_usa_canada: formValues?.legal_protection_usa_canada
      });
    }
  }, [formValues?.legal_protection_usa_canada]);

  useEffect(() => {
    let newTurnover = null;

    if (
      activityTypeWatcher === 'SECU' &&
      formValues?.turnover > RCPHX_TUNROVER_EXEMPTION_THRESHOLD_MAX_SECU
    ) {
      newTurnover = RCPHX_TUNROVER_EXEMPTION_THRESHOLD_MAX_SECU;
    }

    if (newTurnover !== null) {
      form.setFieldsValue({
        turnover: newTurnover
      });
      setFormValues({
        ...formValues,
        turnover: newTurnover
      });
    }
  }, [activityTypeWatcher]);

  useEffect(() => {
    if (!worldwideAllowedActivities.includes(formValues?.activity_type)) {
      form.setFieldsValue({
        turnover_usa_canada: false
      });
      setFormValues({
        ...formValues,
        turnover_usa_canada: false
      });
    }
  }, [worldwideWatcher, activityTypeWatcher]);

  useEffect(() => {
    if (!isRCPHX) return;

    const guaranteeSteps = guaranteeLimitSteps(
      formValues?.activity_detail?.includes(RCPHX_TATOO_ACTIVITY),
      formValues?.activity_type,
      formValues?.turnover,
      hasVtmActivity
    );
    const guaranteeLimitValue =
      form.getFieldValue(['guarantee_limit']) || guaranteeSteps[0].value;

    const guaranteeLimitStep = guaranteeSteps.find(
      ({ value }) => value === guaranteeLimitValue
    );

    let newGuaranteeLimitStep = guaranteeLimitStep;
    if (!guaranteeLimitStep || guaranteeLimitStep?.disabled?.()) {
      newGuaranteeLimitStep = guaranteeSteps.find(
        ({ disabled = () => false }) => !disabled?.()
      );
    } else {
      return;
    }

    form.setFieldsValue({
      guarantee_limit: newGuaranteeLimitStep.value
    });
    setFormValues({
      ...formValues,
      guarantee_limit: newGuaranteeLimitStep.value
    });
  }, [
    isRCPHX,
    turnoverWatcher,
    activityTypeWatcher,
    hasTattooActivity,
    hasVtmActivity
  ]);

  const rcphxFields = [
    {
      name: ['activity_type'],
      tooltip: false,
      rules: [{ required: true }],
      input: (
        <Radio.Group
          buttonStyle="solid"
          onChange={(e) => {
            const newActivityType = e.target.value;
            form.setFieldsValue({
              activity_type: newActivityType,
              activity_detail: []
            });
            setFormValues({
              ...formValues,
              activity_type: newActivityType,
              activity_detail: []
            });
          }}
        >
          {activitiesType.map((activity) => (
            <Radio.Button
              value={activity.value}
              key={`RCPHX_activity_type_${activity.value}`}
            >
              <Space orientation="horizontal">{activity.label}</Space>
            </Radio.Button>
          ))}
        </Radio.Group>
      ),
      step: 0
    },
    {
      name: ['activity_detail'],
      tooltip: false,
      rules: [{ required: true }],
      input: (
        <Select
          className="activities-select"
          popupClassName="activities-select-dropdown"
          mode="multiple"
        >
          {liveRcphxActivitiesDetails[formValues?.activity_type]?.map(
            (activity) => (
              <Option
                value={activity.name}
                key={`RCPHX_activity_detail_${activity.name}`}
              >
                {activity.name}
              </Option>
            )
          )}
        </Select>
      ),
      step: 0
    },
    {
      name: ['turnover'],
      tooltip: {
        name: 'rcphx.turnover',
        params: {
          threshold: numberWithSpaces(
            RCPHX_TUNROVER_EXEMPTION_THRESHOLD_MAX - 1
          )
        }
      },
      ...(RCPHX_MED_BIE_OSTEO_TYPES.includes(formValues?.activity_type)
        ? {
            tooltipLabel:
              "Ce produit est souscriptible en ligne jusqu'à 200 000€ de chiffre d'affaires annuel, sans possibilité de dérogation au-delà."
          }
        : {}),
      rules: [{ required: true }],
      input: (
        <InputNumber
          min={0}
          max={
            RCPHX_MED_BIE_OSTEO_TYPES.includes(formValues?.activity_type)
              ? RCPHX_MED_BIE_OSTEO_MAX_TURNOVER
              : null
          }
          step={1000}
          style={{ width: 120 }}
          formatter={(value) => numberWithSpaces(value)}
        />
      ),
      step: 0
    },
    ...arrayConditionnalElement(
      [
        {
          name: ['turnover_usa_canada'],
          tooltip: true,
          tooltipName: 'turnover_usa_canada',
          input: (
            <Switch
              checkedChildren={t('quotes.form.switch.yes')}
              unCheckedChildren={t('quotes.form.switch.no')}
            />
          ),
          valuePropName: 'checked',
          step: 0
        },
        ...(formValues && formValues.turnover_usa_canada
          ? [
              {
                name: ['turnover_usa_canada_amount'],
                tooltip: true,
                tooltipName: 'turnover_usa_canada_amount',
                input: (
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <Form.Item name={['turnover_usa_canada_amount']}>
                      <InputNumber
                        min={0}
                        defaultValue={0}
                        max={
                          formValues?.turnover_usa_canada_amount_type ===
                          'PERCENTAGE'
                            ? 100
                            : null
                        }
                      />
                    </Form.Item>
                    <Form.Item name={['turnover_usa_canada_amount_type']}>
                      <Select style={{ width: 100 }} defaultValue="CURRENCY">
                        <Option
                          value="PERCENTAGE"
                          key="RCPHX_turnover_usa_canada_amount_PERCENTAGE"
                        >
                          {t('contracts.form.options.PERCENTAGE')}
                        </Option>
                        <Option
                          value="CURRENCY"
                          key="RCPHX_turnover_usa_canada_amount_CURRENCY"
                        >
                          {t('contracts.form.options.CURRENCY')}
                        </Option>
                      </Select>
                    </Form.Item>
                  </div>
                ),
                rules: [
                  { required: true },
                  {
                    validator: () => {
                      return formValues &&
                        formValues.turnover_usa_canada_amount > 0
                        ? Promise.resolve()
                        : Promise.reject(new Error('Ce champs est requis.'));
                    }
                  }
                ],
                step: 0
              }
            ]
          : [])
      ],
      worldwideAllowedActivities.includes(formValues?.activity_type)
    ),

    {
      name: ['guarantee_limit'],
      tooltip: true,
      tooltipLabel: RCPHX_MED_BIE_OSTEO_TYPES.includes(
        formValues?.activity_type
      )
        ? t('contracts.form.tooltip.rcphx.guarantee_limit_fixed')
        : t('contracts.form.tooltip.rcpmk.guarantee_limit', {
            threshold: numeral(
              RCPHX_GUARANTEE_LIMIT_EXEMPTION_THRESHOLD - 1
            ).format()
          }),
      rules: [{ required: true }],
      input:
        ['MED', 'OSTEO', 'BIE'].includes(formValues?.activity_type) ? (
          <Radio.Group buttonStyle="solid" disabled value={8000000}>
            <Radio.Button
              value={8000000}
              key="RCPHX_guarantee_limit_MED_OSTEO_8000000"
            >
              <Space orientation="horizontal">
                {`${numberWithSpaces(8000000)}€`}
              </Space>
            </Radio.Button>
          </Radio.Group>
        ) : (
          <FormRadioButtonWithCustom
            fieldName="guarantee_limit"
            options={guaranteeLimitSteps(
              hasTattooActivity,
              formValues?.activity_type,
              formValues?.turnover,
              hasVtmActivity
            )}
            getIsCustom={({ value }) =>
              value >= RCPHX_GUARANTEE_LIMIT_EXEMPTION_THRESHOLD &&
              formValues?.guarantee_limit >=
                RCPHX_GUARANTEE_LIMIT_EXEMPTION_THRESHOLD
            }
            customInputProps={{
              min: RCPHX_GUARANTEE_LIMIT_EXEMPTION_THRESHOLD,
              max: null
            }}
          />
        ),
      step: 0
    },
    ...arrayConditionnalElement(
      {
        name: ['deductible_amount'],
        tooltip: true,
        tooltipLabel: t('contracts.form.tooltip.rcphx.deductible_amount'),
        label: `${t('contracts.form.deductible_amount_rcphx')}`,
        translate: false,
        input: (
          <Radio.Group buttonStyle="solid" disabled>
            <Radio.Button
              value={estimatedCost?.deductible_amount}
              key={`RCPHX_activity_type_${estimatedCost?.deductible_amount}`}
            >
              <Space orientation="horizontal">
                {`${estimatedCost?.deductible_amount} €`}
              </Space>
            </Radio.Button>
          </Radio.Group>
        ),
        step: 0
      },
      typeof estimatedCost?.deductible_amount === 'number'
    ),
    ...arrayConditionnalElement(
      {
        name: ['deductible_amount_rce'],
        tooltip: true,
        tooltipLabel: t('contracts.form.tooltip.rcphx.deductible_amount_rce'),
        label: `${t('contracts.form.deductible_amount_rce_rcphx')}`,
        translate: false,
        input: (
          <Radio.Group buttonStyle="solid" disabled>
            <Radio.Button
              value={getRCPHXDeductibleAmountRCE(
                formValues?.activity_type,
                formValues?.turnover,
                formValues?.guarantee_limit
              )}
              key={`RCPHX_deductible_amount_rce_${formValues?.activity_type}_${formValues?.turnover}_${formValues?.guarantee_limit}`}
            >
              <Space orientation="horizontal">
                {`${getRCPHXDeductibleAmountRCE(
                  formValues?.activity_type,
                  formValues?.turnover,
                  formValues?.guarantee_limit
                )} €`}
              </Space>
            </Radio.Button>
          </Radio.Group>
        ),
        step: 0
      },
      !!formValues?.exploitation_responsibility &&
        typeof estimatedCost?.deductible_amount_rce === 'number'
    ),
    ...arrayConditionnalElement(
      [
        {
          name: ['deductible_amount_usa_canada'],
          tooltip: true,
          tooltipLabel: t(
            'contracts.form.tooltip.rcphx.deductible_amount_usa_canada'
          ),
          input: (
            <Radio.Group buttonStyle="solid" disabled>
              <Radio.Button
                value={DEDUCTIBLE_AMOUNT_USA_CANADA}
                key={`RCPHX_deductible_amount_usa_canada_${DEDUCTIBLE_AMOUNT_USA_CANADA}`}
              >
                <Space orientation="horizontal">
                  {`${numberWithSpaces(DEDUCTIBLE_AMOUNT_USA_CANADA)} €`}
                </Space>
              </Radio.Button>
            </Radio.Group>
          ),
          step: 0
        }
      ],
      formValues?.turnover_usa_canada
    ),
    {
      name: ['exploitation_responsibility'],
      translate: false,
      label: `${t('contracts.form.exploitation_responsibility')}`,
      input: ['MED', 'OSTEO', 'BIE'].includes(formValues?.activity_type) ? (
        <Switch
          defaultChecked
          disabled
          checkedChildren={t('quotes.form.switch.yes')}
          unCheckedChildren={t('quotes.form.switch.no')}
        />
      ) : (
        <Switch
          checkedChildren={t('quotes.form.switch.yes')}
          unCheckedChildren={t('quotes.form.switch.no')}
        />
      ),
      valuePropName: 'checked',
      tooltip: true,
      step: 0
    },
    {
      name: ['legal_protection_rcphx'],
      label: RCPHX_MED_BIE_OSTEO_TYPES.includes(formValues?.activity_type)
        ? 'legal_protection_rcphx_optional'
        : undefined,
      input: RCPHX_MED_BIE_OSTEO_TYPES.includes(formValues?.activity_type) ? (
        <Switch
          defaultChecked
          checkedChildren={t('quotes.form.switch.yes')}
          unCheckedChildren={t('quotes.form.switch.no')}
        />
      ) : (
        <Switch
          defaultChecked
          disabled
          checkedChildren={t('quotes.form.switch.yes')}
          unCheckedChildren={t('quotes.form.switch.no')}
        />
      ),
      valuePropName: 'checked',
      tooltip: true,
      tooltipLabel: (
        <Trans>{t('contracts.form.tooltip.legal_protection_rcphx')}</Trans>
      ),
      step: 0
    },
    ...arrayConditionnalElement(
      {
        name: ['legal_protection_rcphx_extended'],
        input: (
          <Switch
            checkedChildren={t('quotes.form.switch.yes')}
            unCheckedChildren={t('quotes.form.switch.no')}
          />
        ),
        valuePropName: 'checked',
        tooltip: true,
        tooltipLabel: (
          <Trans>
            {t('contracts.form.tooltip.legal_protection_rcphx_extended')}
          </Trans>
        ),
        step: 0
      },
      RCPHX_MED_BIE_OSTEO_TYPES.includes(formValues?.activity_type) &&
        !!formValues?.legal_protection_rcphx
    ),
    ...arrayConditionnalElement(
      {
        name: ['legal_protection_usa_canada'],
        tooltip: true,
        input: (
          <Switch
            checkedChildren={t('quotes.form.switch.yes')}
            unCheckedChildren={t('quotes.form.switch.no')}
            defaultChecked={!!formValues?.turnover_usa_canada}
            disabled={hasTattooActivity || !!formValues?.turnover_usa_canada}
          />
        ),
        valuePropName: 'checked',
        step: 0
      },
      worldwideAllowedActivities.includes(activityTypeWatcher)
    ),
    [
      {
        name: ['social_mandate_responsibility'],
        tooltip: true,
        input: (
          <Switch
            checkedChildren={t('quotes.form.switch.yes')}
            unCheckedChildren={t('quotes.form.switch.no')}
          />
        ),
        valuePropName: 'checked',
        step: 0
      }
    ],
    ...arrayConditionnalElement(
      [
        {
          name: ['cyber_option_rcphx'],
          tooltip: true,
          tooltipLabel: t('contracts.form.tooltip.rcphx.cyber_option_rcphx'),
          input: (
            <Switch
              checkedChildren={t('quotes.form.switch.yes')}
              unCheckedChildren={t('quotes.form.switch.no')}
            />
          ),
          valuePropName: 'checked',
          step: 0
        },
        ...arrayConditionnalElement(
          [
            {
              name: ['cyber_option_rcphx_extended'],
              tooltip: true,
              tooltipLabel: t(
                'contracts.form.tooltip.rcphx.cyber_option_rcphx_extended'
              ),
              input: (
                <Switch
                  checkedChildren={t('quotes.form.switch.yes')}
                  unCheckedChildren={t('quotes.form.switch.no')}
                />
              ),
              valuePropName: 'checked',
              step: 0
            },
            ...arrayConditionnalElement(
              {
                name: ['cyber_option_rcphx_fraud'],
                tooltip: true,
                tooltipLabel: t(
                  'contracts.form.tooltip.rcphx.cyber_option_rcphx_fraud'
                ),
                input: (
                  <Switch
                    checkedChildren={t('quotes.form.switch.yes')}
                    unCheckedChildren={t('quotes.form.switch.no')}
                  />
                ),
                valuePropName: 'checked',
                step: 0
              },
              !!formValues?.cyber_option_rcphx_extended
            )
          ],
          !!formValues?.cyber_option_rcphx
        )
      ],
      !!(
        cyberOptionAllowedActivities.includes(formValues?.activity_type) &&
        (formValues?.turnover || 0) <= 500_000
      )
    ),
    {
      name: ['fraction_type'],
      rules: [{ required: true }],
      tooltip: true,
      input: (
        <Radio.Group buttonStyle="solid" disabled={isAmendment !== -1}>
          <Radio.Button value="MONTHLY">
            {t('contracts.form.options.MONTHLY')}
          </Radio.Button>
          <Radio.Button value="QUARTERLY">
            {t('contracts.form.options.QUARTERLY')}
          </Radio.Button>
          <Radio.Button value="BIANNUAL">
            {t('contracts.form.options.BIANNUAL')}
          </Radio.Button>
          <Radio.Button value="YEARLY">
            {t('contracts.form.options.YEARLY')}
          </Radio.Button>
        </Radio.Group>
      ),
      step: 0
    },
    ...arrayConditionnalElement(
      {
        name: ['insurance_historic_hiscox'],
        label: getInsuranceHistoricLabel(formValues?.activity_type),
        step: 1
      },
      !RCPHX_MED_BIE_OSTEO_TYPES.includes(formValues?.activity_type)
    ),
    ...(typePrerequisites || []).map((prerequisite) => ({
      name: [prerequisite.name],
      label: prerequisite.label,
      step: 1
    })),
    {
      name: ['history_risk_hiscox'],
      label:
        formValues?.activity_type === 'TAG'
          ? 'history_risk_hiscox_tag'
          : 'history_risk_hiscox',
      step: 1
    }
  ];

  return {
    rcphxFields: rcphxFields.map((field) => {
      if (field.step === 1) {
        const updatedField = { ...field };
        updatedField.label = (
          <Trans>{t(`contracts.form.${field.label || field.name[0]}`)}</Trans>
        );
        return {
          input: <PreRequisitesSwitch />,
          labelAlign: 'left',
          valuePropName: 'checked',
          withDivider: true,
          layout: preRequisitesLayout,
          formattedLabel: true,
          ...updatedField
        };
      }
      return field;
    })
  };
};

export default useRCPHXFields;
